'use client'

import { useRouter } from 'next/navigation'
import { TUser } from '@/libs/types/TUser'
import { ReactNode, createContext, useContext, useEffect, useState } from 'react'
import { appUrls } from '@/libs/appUrls'
import { apiUrls } from '@/libs/apiUrls'
import Cookies from 'js-cookie'
import { useLocale } from '@/features/i18n/hooks/useLocale'

export function getTUser(): TUser | undefined {
  const json = window.sessionStorage.getItem('user') ?? Cookies.get('user')
  return json && JSON.parse(json)
}

export function setTUser(tUser: TUser, remember: boolean) {
  const json = JSON.stringify(tUser)
  const expires = remember ? 30 : 0.5 // 30 or 0.5日
  Cookies.set('user', json, { secure: true, expires: expires, path: '/' })
}

export function getToken(): string | undefined {
  return window.sessionStorage.getItem('token') ?? Cookies.get('token')
}

export function setToken(token: string, remember: boolean) {
  const expires = remember ? 30 : 0.5 // 30 or 0.5日
  Cookies.set('token', token, { secure: true, expires: expires, path: '/' })
}

interface Props {
  tUser: TUser
  token: string
  signOut: () => void
}

const AuthContext = createContext<Props | undefined>(undefined)

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const { locale } = useLocale()
  const router = useRouter()

  const [tUser, setTUser] = useState<TUser>()
  const [token, setToken] = useState<string>()

  useEffect(() => {
    const tUser = getTUser()
    const token = getToken()
    if (tUser && token) {
      setTUser(tUser)
      setToken(token)
    } else {
      router.push(appUrls.login(locale))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const signOut = () => {
    if (!tUser || !token) {
      setTUser(undefined)
      setToken(undefined)
      window.sessionStorage.removeItem('user')
      window.sessionStorage.removeItem('token')

      router.push(appUrls.login(locale))
      return
    }

    const data = {
      userId: tUser.id,
      roleId: 30, // 研究者ユーザーと企業ユーザーの設定値を分ける処理(研究者: 30, 企業: 200)
      token: token,
      pageViewCount: 0,
    }

    setTUser(undefined)
    setToken(undefined)
    window.sessionStorage.removeItem('user')
    window.sessionStorage.removeItem('token')
    Cookies.remove('user')
    Cookies.remove('token')

    fetch(apiUrls.auth.logout(), {
      method: 'post',
      body: JSON.stringify(data),
      headers: { Accept: 'application/json', 'Content-Type': 'application/json;charset=utf-8' },
      cache: 'no-store',
    })

    router.push(appUrls.login(locale))
  }

  const context = {
    tUser: tUser!,
    token: token!,
    signOut,
  }
  return <AuthContext.Provider value={context}>{children}</AuthContext.Provider>
}

export const useAuth = (): Props => {
  const context = useContext(AuthContext)

  if (!context) {
    throw new Error('context is missing')
  }

  return context
}
